// 股市页公司详情里的「挂着的单」:盘口(卖五到卖一、买一到买五,中间最新价和买一卖一差多少)、深度图(价格横着,
// 买单从高到低累计、卖单从低到高累计,鼠标停上去看到这个价能卖、能买多少股、合多少钱),每一张单折起来放在下面。
// 数从引擎来(engine/web/src/stocks.rs 的 mestock:depth 是簿上没过期的单按价并成的档 [每股, 股数, 几张, 有没有我的],
// orders 是最高的几张买单、最低的几张卖单;照原来一个月碰一次头的规矩两样都是 null,不画)。只看,下单在别处。
import { dateOf, esc, money } from './sbchart.ts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type J = any;
type Level = [number, number, number, boolean];

const px = (v: number) => v >= 100 ? v.toFixed(0) : v.toFixed(2);
const nTxt = (n: number) => n < 10 ? n.toFixed(2) : n.toFixed(0);
const sharesTxt = (n: number) => (n >= 1e4 ? money(n).replace(' 元', '') : nTxt(n));
const pct = (v: number) => `${(v * 100).toFixed(v < 0.01 ? 2 : 1)}%`;
const who = (w: J) => w?.kind === 'person' ? `<a data-go="person:${w.id}">${esc(w.name)}</a>` : w?.kind === 'firm' ? `<a data-go="firm:${w.id}">${esc(w.name)}</a>` : esc(w?.name ?? '');

const W = 640, H = 200, L = 8, R = 8, T = 12, B = 26;
/** 鼠标(手指)停在哪个价上:刷新以后还在。key 是哪一家。 */
let dHover: { key: string; p: number } | null = null;

/** 盘口:卖五到卖一(上)、最新价和差价(中)、买一到买五(下);档数不够的照实列。 */
const ladder = (bids: Level[], asks: Level[], last: number, traded: boolean) => {
  const row = (name: string, l: Level, cls: string) => `<tr class="${cls}${l[3] ? ' mine' : ''}"><td>${name}</td><td>${px(l[0])}</td><td>${sharesTxt(l[1])}</td><td>${l[2]} 张${l[3] ? ' · 有我的' : ''}</td></tr>`;
  const sells = asks.slice(0, 5).map((l, i) => row(`卖${'一二三四五'[i]}`, l, 'pk-ask')).reverse().join('');
  const buys = bids.slice(0, 5).map((l, i) => row(`买${'一二三四五'[i]}`, l, 'pk-bid')).join('');
  const b1 = bids[0]?.[0], a1 = asks[0]?.[0];
  const gap = b1 != null && a1 != null ? `差价 ${px(a1 - b1)} 元 · 差 ${pct((a1 - b1) / ((a1 + b1) / 2))}` : b1 == null && a1 == null ? '两边都没人挂单' : b1 == null ? '没人挂买单' : '没人挂卖单';
  const mid = `<tr class="pk-mid"><td colspan="4"><b>${px(last)} 元</b> ${traded ? '最新成交' : '近三个月没成交,按估值'} · ${gap}</td></tr>`;
  return `<table class="sb-pk"><thead><tr><th></th><th>每股</th><th>股数</th><th>单</th></tr></thead><tbody>${sells || '<tr class="pk-none"><td colspan="4">没人挂卖单</td></tr>'}${mid}${buys || '<tr class="pk-none"><td colspan="4">没人挂买单</td></tr>'}</tbody></table>`;
};

/** 深度图的价格范围:两边的档都放进来,离中间太远的(低于一半、高于两倍半)截掉。 */
const domain = (bids: Level[], asks: Level[], last: number) => {
  const b1 = bids[0]?.[0], a1 = asks[0]?.[0];
  const mid = b1 != null && a1 != null ? (b1 + a1) / 2 : b1 ?? a1 ?? last;
  let lo = Math.max(mid * 0.4, bids.length ? bids[bids.length - 1][0] : Math.min(mid, last) * 0.9);
  let hi = Math.min(mid * 2.5, asks.length ? asks[asks.length - 1][0] : Math.max(mid, last) * 1.1);
  if (!bids.length) lo = Math.min(lo, (a1 ?? mid) * 0.85);
  if (!asks.length) hi = Math.max(hi, (b1 ?? mid) * 1.15);
  const pad = (hi - lo) * 0.04 || mid * 0.05 || 0.01;
  return { lo: Math.max(0, lo - pad), hi: hi + pad, mid };
};

/** 深度图:买单往左累计(左边一条往右下的台阶)、卖单往右累计(右边一条往右上的台阶),中间一条最新价。 */
const chart = (bids: Level[], asks: Level[], last: number, redUp: boolean, key: string) => {
  const { lo, hi } = domain(bids, asks, last);
  const pw = W - L - R, ph = H - T - B;
  const x = (p: number) => L + pw * (p - lo) / (hi - lo);
  const inB = bids.filter((l) => l[0] >= lo), inA = asks.filter((l) => l[0] <= hi);
  const cum = (ls: Level[]) => { let c = 0; return ls.map((l) => (c += l[1])); };
  const cb = cum(inB), ca = cum(inA);
  const top = Math.max(1, cb[cb.length - 1] ?? 0, ca[ca.length - 1] ?? 0) * 1.08;
  const y = (q: number) => T + ph * (1 - q / top);
  const step = (ls: Level[], c: number[], edge: number) => {
    if (!ls.length) return '';
    let d = `M${x(ls[0][0]).toFixed(1)},${y(0).toFixed(1)}`;
    ls.forEach((l, i) => {
      d += ` L${x(l[0]).toFixed(1)},${y(c[i]).toFixed(1)}`;
      const nx = i + 1 < ls.length ? ls[i + 1][0] : edge;
      d += ` L${x(nx).toFixed(1)},${y(c[i]).toFixed(1)}`;
    });
    return `${d} L${x(edge).toFixed(1)},${y(0).toFixed(1)} Z`;
  };
  const bidCls = redUp ? 'up' : 'down', askCls = redUp ? 'down' : 'up';
  let g = '';
  // 股数的刻度(左边三格)和价格的刻度(下面五个)
  for (let k = 1; k <= 3; k++) { const q = top / 1.08 * k / 3; g += `<line class="grid" x1="${L}" x2="${W - R}" y1="${y(q).toFixed(1)}" y2="${y(q).toFixed(1)}"/><text class="t" x="${W - R - 2}" y="${(y(q) - 3).toFixed(1)}" text-anchor="end">${esc(sharesTxt(q))} 股</text>`; }
  for (let k = 0; k <= 4; k++) { const p = lo + (hi - lo) * (k + 0.5) / 5; g += `<text class="t" x="${x(p).toFixed(1)}" y="${H - 6}" text-anchor="middle">${px(p)}</text>`; }
  g += `<path class="dside ${bidCls}" d="${step(inB, cb, lo)}"/><path class="dside ${askCls}" d="${step(inA, ca, hi)}"/>`;
  if (last >= lo && last <= hi) g += `<line class="dlast" x1="${x(last).toFixed(1)}" x2="${x(last).toFixed(1)}" y1="${T}" y2="${T + ph}"/><text class="t" x="${(x(last) + 3).toFixed(1)}" y="${T + 9}">最新 ${px(last)}</text>`;
  // 一边一张单都没有:那半边空着,写一句
  const b1 = bids[0]?.[0], a1 = asks[0]?.[0];
  const midX = x(b1 != null && a1 != null ? (b1 + a1) / 2 : b1 ?? a1 ?? last);
  if (!inB.length) g += `<text class="dnone" x="${((L + midX) / 2).toFixed(1)}" y="${(T + ph / 2).toFixed(1)}" text-anchor="middle">眼下没人挂买单</text>`;
  if (!inA.length) g += `<text class="dnone" x="${((midX + W - R) / 2).toFixed(1)}" y="${(T + ph / 2).toFixed(1)}" text-anchor="middle">眼下没人挂卖单</text>`;
  const at = dHover?.key === key && dHover.p >= lo && dHover.p <= hi ? dHover.p : null;
  const hx = at != null ? x(at).toFixed(1) : '0';
  g += `<line class="dx" x1="${hx}" x2="${hx}" y1="${T}" y2="${T + ph}" visibility="${at != null ? 'visible' : 'hidden'}"/>`;
  const tip = at != null ? `<div class="sb-dtip" style="${tipPos(x(at))}">${tipAt(bids, asks, at)}</div>` : '<div class="sb-dtip" hidden></div>';
  return `<div class="sb-dwrap"><svg class="sb-depth" viewBox="0 0 ${W} ${H}" data-key="${esc(key)}" data-lo="${lo}" data-hi="${hi}" data-b='${JSON.stringify(bids)}' data-a='${JSON.stringify(asks)}'>${g}</svg>${tip}</div>`;
};

/** 每一张单(最高的几张买单、最低的几张卖单):谁挂的、到哪天、要价、股数。 */
const side = (rows: J[], title: string, price: string, empty: string) => `<table class="sb-sd-table sb-book-side"><thead><tr><th>${title}</th><th>${price}</th><th>股数</th></tr></thead><tbody>${
  rows.length ? rows.map((o) => `<tr${o.mine ? ' class="sb-book-mine"' : ''}><td>${o.mine ? '我' : who(o.who)}<small>到 ${esc(dateOf(o.until))}</small></td><td class="nw">${px(o.px)} 元</td><td class="nw">${nTxt(o.qty)}<small>${pct(o.pct)}</small></td></tr>`).join('')
    : `<tr><td colspan="3"><small>${empty}</small></td></tr>`}</tbody></table>`;

/** 挂着的单:盘口、深度图、每一张单(折起来)。depth 是 null(照原来的规矩)返回空。 */
export const depthHTML = (depth: J | null, orders: J | null, last: number, traded: boolean, redUp: boolean, key = '', open = false) => {
  // key 是这一家(深度图停着的价、每一张单展没展开都按它记)
  if (!depth) return '';
  const bids = (depth.bids ?? []) as Level[], asks = (depth.asks ?? []) as Level[];
  const each = orders ? `<details class="sb-val-terms"${key ? ` data-key="${esc(key)}"` : ''}${open ? ' open' : ''}><summary>每一张单</summary><div class="sb-book">${side(orders.bids ?? [], '想买的', '每股出', '没人挂买单')}${side(orders.asks ?? [], '想卖的', '每股要', '没人挂卖单')}</div></details>` : '';
  return `<h4>挂着的单<small>新挂的单当场和对面最好的价成交,按先挂的那张的价;没成交的挂三十天</small></h4>
    <div class="sb-pkwrap">${ladder(bids, asks, last, traded)}${chart(bids, asks, last, redUp, key)}</div>${each}`;
};

/** 浮框放在竖线的哪边:左半边放右边,右半边放左边。 */
const tipPos = (vx: number) => { const frac = vx / W; return frac > 0.5 ? `right:calc(${((1 - frac) * 100).toFixed(1)}% + 12px)` : `left:calc(${(frac * 100).toFixed(1)}% + 12px)`; };

/** 价 p 处:买单那边(不高于买一)累计能卖多少,卖单那边(不低于卖一)累计能买多少;中间写买一卖一。 */
const tipAt = (bids: Level[], asks: Level[], p: number) => {
  const b1 = bids[0]?.[0], a1 = asks[0]?.[0];
  const sum = (ls: Level[], ok: (q: number) => boolean) => ls.filter((l) => ok(l[0])).reduce((s, l) => [s[0] + l[1], s[1] + l[0] * l[1], s[2] + l[2]], [0, 0, 0]);
  const row = (k: string, v: string) => `<div>${k}<span>${v}</span></div>`;
  if (b1 != null && p <= b1) {
    const [q, m, n] = sum(bids, (x) => x >= p);
    return `<b>卖到 ${px(p)} 元</b>${row('能卖', `${sharesTxt(q)} 股`)}${row('得', money(m))}${row('买单', `${n} 张`)}`;
  }
  if (a1 != null && p >= a1) {
    const [q, m, n] = sum(asks, (x) => x <= p);
    return `<b>买到 ${px(p)} 元</b>${row('能买', `${sharesTxt(q)} 股`)}${row('花', money(m))}${row('卖单', `${n} 张`)}`;
  }
  return `<b>${px(p)} 元</b>${row('买一', b1 != null ? `${px(b1)} 元` : '没人挂')}${row('卖一', a1 != null ? `${px(a1)} 元` : '没人挂')}`;
};

const show = (svg: SVGSVGElement, clientX: number) => {
  const tip = svg.parentElement?.querySelector('.sb-dtip') as HTMLElement | null;
  const line = svg.querySelector('.dx');
  if (!tip || !line) return;
  const r = svg.getBoundingClientRect();
  const lo = Number(svg.dataset.lo), hi = Number(svg.dataset.hi);
  const vx = (clientX - r.left) / r.width * W;
  if (vx < L || vx > W - R) { hide(svg); return; }
  const p = lo + (hi - lo) * (vx - L) / (W - L - R);
  dHover = { key: svg.dataset.key ?? '', p };
  line.setAttribute('x1', vx.toFixed(1)); line.setAttribute('x2', vx.toFixed(1)); line.setAttribute('visibility', 'visible');
  tip.innerHTML = tipAt(JSON.parse(svg.dataset.b ?? '[]') as Level[], JSON.parse(svg.dataset.a ?? '[]') as Level[], p);
  tip.setAttribute('style', tipPos(vx));
  tip.hidden = false;
};
const hide = (svg: SVGSVGElement) => {
  dHover = null;
  svg.querySelector('.dx')?.setAttribute('visibility', 'hidden');
  const tip = svg.parentElement?.querySelector('.sb-dtip') as HTMLElement | null;
  if (tip) tip.hidden = true;
};

/** 装上深度图的悬停(手机上点一下,点别处收起)。整页只装一回。 */
export const installDepth = () => {
  document.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse') return;
    const svg = (e.target as Element).closest?.('svg.sb-depth') as SVGSVGElement | null;
    if (svg) show(svg, e.clientX);
  });
  document.addEventListener('pointerout', (e) => {
    if (e.pointerType !== 'mouse') return;
    const svg = (e.target as Element).closest?.('svg.sb-depth') as SVGSVGElement | null;
    if (svg && !svg.contains(e.relatedTarget as Node)) hide(svg);
  });
  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse') return;
    const svg = (e.target as Element).closest?.('svg.sb-depth') as SVGSVGElement | null;
    document.querySelectorAll<SVGSVGElement>('svg.sb-depth').forEach((s) => { if (s !== svg) hide(s); });
    if (svg) show(svg, e.clientX);
  });
};
